import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { User, Package, LogOut, Mail, Lock, Eye, EyeOff, Settings } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import Button from '../components/ui/Button'
import Input from '../components/ui/Input'

export default function Account() {
  const { user, isAuthenticated, isAdmin, isLoading, signIn, signUp, signOut } = useAuth()
  const navigate = useNavigate()
  const [isSignUp, setIsSignUp] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
  })

  // Redirect admin users to admin dashboard
  useEffect(() => {
    if (isAuthenticated && isAdmin) {
      navigate('/admin')
    }
  }, [isAuthenticated, isAdmin, navigate])

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
    setError('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      if (isSignUp) {
        await signUp(formData.email, formData.password, {
          first_name: formData.firstName,
          last_name: formData.lastName,
        })
      } else {
        await signIn(formData.email, formData.password)
      }
    } catch (err) {
      setError(err.message || 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  const handleSignOut = async () => {
    try {
      await signOut()
      navigate('/')
    } catch (err) {
      console.error('Error signing out:', err)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-pp-ivory flex items-center justify-center">
        <div className="animate-pulse text-pp-stone">Loading...</div>
      </div>
    )
  }

  // Authenticated View
  if (isAuthenticated) {
    return (
      <div className="min-h-screen bg-pp-ivory">
        <div className="container mx-auto px-4 lg:px-8 py-8 lg:py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-display text-display-md text-pp-ink mb-8">
              My Account
            </h1>

            <div className="grid md:grid-cols-3 gap-6">
              {/* Profile Card */}
              <div className="bg-white rounded-2xl p-6 border border-pp-sand/50">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-full bg-pp-sage/20 flex items-center justify-center">
                    <User className="w-7 h-7 text-pp-sage" />
                  </div>
                  <div>
                    <p className="font-medium text-pp-ink">
                      {user?.user_metadata?.first_name || 'Welcome'}
                    </p>
                    <p className="text-sm text-pp-stone">{user?.email}</p>
                  </div>
                </div>
                <button
                  onClick={handleSignOut}
                  className="flex items-center gap-2 text-sm text-pp-stone hover:text-pp-terracotta transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  Sign Out
                </button>
              </div>

              {/* Admin Dashboard Link - Only for admins */}
              {isAdmin && (
                <Link
                  to="/admin"
                  className="bg-white rounded-2xl p-6 border border-pp-terracotta/50 hover:border-pp-terracotta transition-colors group"
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-14 h-14 rounded-full bg-pp-terracotta/20 flex items-center justify-center">
                      <Settings className="w-7 h-7 text-pp-terracotta" />
                    </div>
                  </div>
                  <h3 className="font-display text-lg text-pp-ink group-hover:text-pp-terracotta transition-colors">
                    Admin Dashboard
                  </h3>
                  <p className="text-sm text-pp-stone mt-1">
                    Manage products, orders & inventory
                  </p>
                </Link>
              )}

              {/* Orders Link */}
              <Link
                to="/account/orders"
                className="bg-white rounded-2xl p-6 border border-pp-sand/50 hover:border-pp-terracotta/50 transition-colors group"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 rounded-full bg-pp-terracotta/10 flex items-center justify-center">
                    <Package className="w-7 h-7 text-pp-terracotta" />
                  </div>
                </div>
                <h3 className="font-display text-lg text-pp-ink group-hover:text-pp-terracotta transition-colors">
                  Order History
                </h3>
                <p className="text-sm text-pp-stone mt-1">
                  View and track your orders
                </p>
              </Link>

              {/* Account Settings */}
              <div className="bg-white rounded-2xl p-6 border border-pp-sand/50">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 rounded-full bg-pp-butter/30 flex items-center justify-center">
                    <Mail className="w-7 h-7 text-pp-butter" />
                  </div>
                </div>
                <h3 className="font-display text-lg text-pp-ink">
                  Account Settings
                </h3>
                <p className="text-sm text-pp-stone mt-1">
                  Manage your preferences
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    )
  }

  // Sign In / Sign Up View
  return (
    <div className="min-h-screen bg-pp-ivory">
      <div className="container mx-auto px-4 lg:px-8 py-12 lg:py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md mx-auto"
        >
          <div className="text-center mb-8">
            <h1 className="font-display text-display-md text-pp-ink mb-2">
              {isSignUp ? 'Create Account' : 'Welcome Back'}
            </h1>
            <p className="text-pp-stone">
              {isSignUp
                ? 'Join Petite Palate for exclusive offers'
                : 'Sign in to view your orders and more'}
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-2xl p-6 lg:p-8 border border-pp-sand/50"
          >
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-lg text-red-600 text-sm">
                {error}
              </div>
            )}

            <div className="space-y-4">
              {isSignUp && (
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="First Name"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                  />
                  <Input
                    label="Last Name"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                  />
                </div>
              )}

              <Input
                label="Email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />

              <div className="relative">
                <Input
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-9 text-pp-stone hover:text-pp-charcoal"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>

              {!isSignUp && (
                <div className="text-right">
                  <button
                    type="button"
                    className="text-sm text-pp-terracotta hover:underline"
                  >
                    Forgot password?
                  </button>
                </div>
              )}
            </div>

            <Button type="submit" className="w-full mt-6" isLoading={loading}>
              {isSignUp ? 'Create Account' : 'Sign In'}
            </Button>

            <p className="text-center text-sm text-pp-stone mt-6">
              {isSignUp ? 'Already have an account?' : "Don't have an account?"}{' '}
              <button
                type="button"
                onClick={() => {
                  setIsSignUp(!isSignUp)
                  setError('')
                }}
                className="text-pp-terracotta hover:underline"
              >
                {isSignUp ? 'Sign In' : 'Create one'}
              </button>
            </p>
          </form>
        </motion.div>
      </div>
    </div>
  )
}
